import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Printer, FileDown, X } from "lucide-react";
import { api } from "../../lib/api";
import { fmtDate, photoUrl, rupee } from "../../lib/customers";
import { Button } from "../ui/button";

const Shell = ({ cid, label, onClose, options, children }) => {
  const doPrint = (kind) => {
    api.post(`/customers/${cid}/audit`, { action: "print", detail: `${label} (${kind})` }).catch(() => {});
    window.print();
  };
  return createPortal(
    <div className="print-overlay fixed inset-0 z-[100] bg-slate-800/70 overflow-y-auto" data-testid="print-preview">
      <div className="no-print sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex flex-wrap items-center gap-3 justify-between">
        <div className="font-semibold text-slate-900">{label} — Print Preview (A4)</div>
        <div className="flex flex-wrap items-center gap-3 text-sm">{options}</div>
        <div className="flex gap-2">
          <Button onClick={() => doPrint("print")} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="print-btn"><Printer className="w-4 h-4 mr-1" /> Print</Button>
          <Button variant="outline" onClick={() => doPrint("pdf")} data-testid="print-pdf-btn"><FileDown className="w-4 h-4 mr-1" /> Save as PDF</Button>
          <Button variant="outline" onClick={onClose} data-testid="print-cancel-btn"><X className="w-4 h-4 mr-1" /> Cancel</Button>
        </div>
      </div>
      <div className="print-sheet bg-white mx-auto my-6 shadow-xl text-[12px] text-slate-900" style={{ width: "210mm", minHeight: "297mm", padding: "14mm" }}>{children}</div>
    </div>,
    document.body,
  );
};

const Header = ({ title }) => (
  <div className="flex items-end justify-between border-b-2 border-teal-700 pb-2 mb-4">
    <div><div className="text-xl font-bold text-teal-800">Ashish Point</div><div className="text-[10px] text-slate-500">CSC & Online Services</div></div>
    <div className="text-right"><div className="text-sm font-semibold uppercase tracking-wider">{title}</div><div className="text-[10px] text-slate-500">Printed: {fmtDate(new Date().toISOString(), true)}</div></div>
  </div>
);

const KV = ({ rows }) => (
  <table className="w-full border-collapse"><tbody>
    {rows.map(([k, v]) => <tr key={k}><td className="border border-slate-300 px-2 py-1 w-44 bg-slate-50 font-medium">{k}</td><td className="border border-slate-300 px-2 py-1">{v || "—"}</td></tr>)}
  </tbody></table>
);

const H = ({ children }) => <div className="mt-5 mb-2 text-[13px] font-bold uppercase tracking-wider text-teal-800">{children}</div>;

const fullAddress = (c) => [c.address, c.village, c.post_office && `PO ${c.post_office}`, c.panchayat, c.block, c.district, c.state, c.pin].filter(Boolean).join(", ");

export const CustomerPrint = ({ c, notes, works, onClose }) => {
  const [mode, setMode] = useState("all");
  const [order, setOrder] = useState("old");
  const [selected, setSelected] = useState(() => new Set(notes.map((n) => n.id)));
  const [summary, setSummary] = useState(false);
  const list = (mode === "all" ? notes : notes.filter((n) => selected.has(n.id))).slice();
  if (order === "new") list.reverse();
  const toggle = (id) => { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); };
  const options = (
    <>
      <label className="flex items-center gap-1"><input type="radio" checked={mode === "all"} onChange={() => setMode("all")} data-testid="print-all-notes" /> All Notes</label>
      <label className="flex items-center gap-1"><input type="radio" checked={mode === "selected"} onChange={() => setMode("selected")} data-testid="print-selected-notes" /> Selected Notes</label>
      <select value={order} onChange={(e) => setOrder(e.target.value)} className="h-8 border rounded px-2" data-testid="print-order"><option value="old">Purane pehle</option><option value="new">Naye pehle</option></select>
      <label className="flex items-center gap-1"><input type="checkbox" checked={summary} onChange={(e) => setSummary(e.target.checked)} data-testid="print-include-summary" /> Include Service Summary</label>
    </>
  );
  return (
    <Shell cid={c.id} label="Customer Details" onClose={onClose} options={options}>
      <Header title="Customer Details" />
      <H>A. Personal Details</H>
      <div className="flex gap-4 items-start">
        <div className="flex-1"><KV rows={[["Customer ID", c.customer_code], ["Customer Name", c.name], ["Father's Name", c.father_name], ["Mother's Name", c.mother_name],
          ["Date of Birth", c.dob && fmtDate(c.dob)], ["Mobile Number", c.mobile], ["Email ID", c.email], ["Full Address", fullAddress(c)], ["Educational Qualification", c.qualification]]} /></div>
        {photoUrl(c) && <img src={photoUrl(c)} alt="" className="w-28 h-32 object-cover border border-slate-300" data-testid="print-photo" />}
      </div>
      <H>B. Customer Notes</H>
      <table className="w-full border-collapse" data-testid="print-notes-table">
        <thead><tr className="bg-slate-100">{["Date", "Note Details", "Follow-up", "Related Work"].map((h) => <th key={h} className="border border-slate-300 px-2 py-1 text-left">{h}</th>)}</tr></thead>
        <tbody>
          {list.map((n) => (
            <tr key={n.id}>
              <td className="border border-slate-300 px-2 py-1 whitespace-nowrap">
                {mode === "selected" && <input type="checkbox" className="no-print mr-1" checked={selected.has(n.id)} onChange={() => toggle(n.id)} />}
                {fmtDate(n.created_at, true)}
              </td>
              <td className="border border-slate-300 px-2 py-1 whitespace-pre-wrap">{n.text}</td>
              <td className="border border-slate-300 px-2 py-1 whitespace-nowrap">{n.followup_at ? fmtDate(n.followup_at) : "—"}</td>
              <td className="border border-slate-300 px-2 py-1">{n.work_name || "—"}</td>
            </tr>
          ))}
          {!list.length && <tr><td colSpan={4} className="border border-slate-300 px-2 py-3 text-center text-slate-500">No notes</td></tr>}
        </tbody>
      </table>
      {mode === "selected" && <div className="no-print mt-2 text-xs text-slate-500">Selected mode: niche se notes chunein —
        {notes.map((n) => <label key={n.id} className="ml-2 inline-flex items-center gap-1"><input type="checkbox" checked={selected.has(n.id)} onChange={() => toggle(n.id)} data-testid={`print-note-check-${n.id}`} />{n.text.slice(0, 20)}</label>)}</div>}
      {summary && (<>
        <H>C. Service Summary</H>
        <table className="w-full border-collapse" data-testid="print-summary-table">
          <thead><tr className="bg-slate-100">{["Work ID", "Service", "Application No", "Date", "Status"].map((h) => <th key={h} className="border border-slate-300 px-2 py-1 text-left">{h}</th>)}</tr></thead>
          <tbody>{works.map((w) => <tr key={w.id}>{[w.work_code, w.service_name, w.application_no || "—", fmtDate(w.application_date || w.created_at), w.status].map((v, i) => <td key={i} className="border border-slate-300 px-2 py-1">{v}</td>)}</tr>)}</tbody>
        </table>
      </>)}
    </Shell>
  );
};

export const WorkPrint = ({ c, w, onClose }) => (
  <Shell cid={c.id} label={`Work ${w.work_code}`} onClose={onClose}>
    <Header title="Work Details" />
    <H>Customer</H>
    <KV rows={[["Customer ID", c.customer_code], ["Name", c.name], ["Mobile", c.mobile]]} />
    <H>Work</H>
    <KV rows={[["Work ID", w.work_code], ["Service", `${w.service_name}${w.category ? ` · ${w.category}` : ""}`], ["Description", w.description],
      ["Application Number", w.application_no], ["Registration Number", w.registration_no], ["Application Date", w.application_date && fmtDate(w.application_date)],
      ["Last Date", w.last_date && fmtDate(w.last_date)], ["Status", w.status], ["Branch", w.branch_name], ["Completed", w.completed_at && fmtDate(w.completed_at, true)]]} />
    <H>Payment</H>
    <KV rows={[["Government Fee", rupee(w.govt_fee)], ["Service Charge", rupee(w.service_charge)], ["Total Amount", rupee(w.total_amount)],
      ["Paid Amount", rupee(w.paid_amount)], ["Due Amount", rupee(w.due_amount)], ["Payment Status", w.payment_status]]} />
  </Shell>
);
